/**
 * Lab Management - Admin interface for lab catalog CRUD operations
 */

import React, { useState, useEffect } from 'react';
import { AdminService, AdminLab, AdminLabListResponse } from '../../services/admin';
import { DataTable, Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Alert } from '../../components/ui/Alert';
import { Input } from '../../components/ui/Input';

export function LabManagement() {
  const [labs, setLabs] = useState<AdminLab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLabs, setSelectedLabs] = useState<Set<string>>(new Set());
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0
  });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingLab, setEditingLab] = useState<AdminLab | null>(null);
  const [deletingLab, setDeletingLab] = useState<AdminLab | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    summary: '',
    os: 'ubuntu-22-04-x64',
    droplet_size: 's-2vcpu-2gb',
    difficulty: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
    estimated_minutes: 60,
    tags: [] as string[],
    prerequisites: [] as string[],
    manifest: {} as Record<string, any>
  });
  const [tagInput, setTagInput] = useState('');
  const [prerequisiteInput, setPrerequisiteInput] = useState('');
  const [manifestInput, setManifestInput] = useState('');
  const [forceDelete, setForceDelete] = useState(false);

  useEffect(() => {
    loadLabs();
  }, [pagination.current, pagination.pageSize, searchQuery, difficultyFilter]);

  const loadLabs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize
      };
      
      if (searchQuery) params.search = searchQuery;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      
      const response: AdminLabListResponse = await AdminService.getLabList(params);
      
      setLabs(response.labs);
      setPagination(prev => ({ ...prev, total: response.total_count }));
      setSelectedLabs(new Set()); // Clear selections on new load
    } catch (err) {
      console.error('Failed to load labs:', err);
      setError('Failed to load labs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      title: '',
      summary: '',
      os: 'ubuntu-22-04-x64',
      droplet_size: 's-2vcpu-2gb',
      difficulty: 'beginner',
      estimated_minutes: 60,
      tags: [],
      prerequisites: [],
      manifest: {}
    });
    setTagInput('');
    setPrerequisiteInput('');
    setManifestInput('');
  };

  const handleCreateLab = async () => {
    try {
      setError(null);
      
      let manifest = {};
      if (manifestInput.trim()) {
        try {
          manifest = JSON.parse(manifestInput);
        } catch (e) {
          setError('Invalid JSON in manifest field');
          return;
        }
      }
      
      await AdminService.createLab({
        ...formData,
        manifest
      });
      
      setShowCreateModal(false);
      resetForm();
      await loadLabs();
    } catch (err: any) {
      console.error('Failed to create lab:', err);
      setError(err.message || 'Failed to create lab. Please try again.');
    }
  };

  const handleEditLab = async () => {
    if (!editingLab) return;
    
    try {
      setError(null);
      
      let manifest;
      if (manifestInput.trim()) {
        try {
          manifest = JSON.parse(manifestInput);
        } catch (e) {
          setError('Invalid JSON in manifest field');
          return;
        }
      }
      
      const updateData: any = { ...formData };
      if (manifest) updateData.manifest = manifest;
      
      await AdminService.updateLab(editingLab.slug, updateData);
      
      setShowEditModal(false);
      setEditingLab(null);
      resetForm();
      await loadLabs();
    } catch (err: any) {
      console.error('Failed to update lab:', err);
      setError(err.message || 'Failed to update lab. Please try again.');
    }
  };

  const handleDeleteLab = async () => {
    if (!deletingLab) return;
    
    try {
      setError(null);
      
      await AdminService.deleteLab(deletingLab.slug, forceDelete);
      
      setShowDeleteModal(false);
      setDeletingLab(null);
      setForceDelete(false);
      await loadLabs();
    } catch (err: any) {
      console.error('Failed to delete lab:', err);
      setError(err.message || 'Failed to delete lab. Please try again.');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.some(tag => tag === tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const addPrerequisite = () => {
    if (prerequisiteInput.trim() && !formData.prerequisites.some(item => item === prerequisiteInput.trim())) {
      setFormData(prev => ({
        ...prev,
        prerequisites: [...prev.prerequisites, prerequisiteInput.trim()]
      }));
      setPrerequisiteInput('');
    }
  };

  const removePrerequisite = (prereq: string) => {
    setFormData(prev => ({
      ...prev,
      prerequisites: prev.prerequisites.filter(p => p !== prereq)
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDifficultyBadgeVariant = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'success';
      case 'intermediate': return 'warning';
      case 'advanced': return 'danger';
      default: return 'default';
    }
  };

  const columns: Column<AdminLab>[] = [
    {
      key: 'title',
      title: 'Lab',
      sortable: true,
      render: (value, lab) => (
        <div>
          <div className="font-medium text-white">{value}</div>
          <div className="text-sm text-gray-400">{lab.slug}</div>
          {lab.summary && (
            <div className="text-xs text-gray-500 mt-1 line-clamp-2">{lab.summary}</div>
          )}
        </div>
      )
    },
    {
      key: 'difficulty',
      title: 'Difficulty',
      sortable: true,
      render: (value) => (
        <Badge variant={getDifficultyBadgeVariant(value)} className="capitalize">
          {value}
        </Badge>
      )
    },
    {
      key: 'estimated_minutes',
      title: 'Duration',
      render: (value) => (
        <span className="text-gray-300">{value} min</span>
      ),
      align: 'center'
    },
    {
      key: 'total_runs',
      title: 'Usage',
      render: (value, lab) => (
        <div className="text-center">
          <div className="text-white font-medium">{lab.active_runs}</div>
          <div className="text-xs text-gray-400">active</div>
          <div className="text-xs text-gray-400">{value} total</div>
        </div>
      ),
      align: 'center'
    },
    {
      key: 'os',
      title: 'OS',
      render: (value) => (
        <span className="text-gray-300 text-sm">{value}</span>
      )
    },
    {
      key: 'droplet_size',
      title: 'Size',
      render: (value) => (
        <span className="text-gray-300 text-sm">{value}</span>
      )
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      render: (value) => (
        <span className="text-gray-300">{formatDate(value)}</span>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, lab) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditingLab(lab);
              setFormData({
                slug: lab.slug,
                title: lab.title,
                summary: lab.summary || '',
                os: lab.os,
                droplet_size: lab.droplet_size,
                difficulty: lab.difficulty,
                estimated_minutes: lab.estimated_minutes,
                tags: lab.tags,
                prerequisites: lab.prerequisites,
                manifest: {}
              });
              setShowEditModal(true);
            }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              setDeletingLab(lab);
              setShowDeleteModal(true);
            }}
          >
            Delete
          </Button>
        </div>
      ),
      align: 'right'
    }
  ];

  return (
    <div className="p-8 bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Lab Management</h1>
            <p className="text-gray-400 mt-1">
              Manage lab catalog, configurations, and usage analytics
            </p>
          </div>
          <Button 
            variant="primary"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
          >
            Create Lab
          </Button>
        </div>

        {error && (
          <Alert
            variant="error"
            title="Error"
            className="mb-6"
          >
            {error}
          </Alert>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">All Difficulties</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          
          <Button
            variant="secondary"
            onClick={() => {
              setSearchQuery('');
              setDifficultyFilter('');
            }}
          >
            Clear Filters
          </Button>
        </div>

        {/* Labs Table */}
        <DataTable
          data={labs}
          columns={columns}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search labs..."
          onSearch={handleSearch}
          selectable={true}
          selectedItems={selectedLabs}
          onSelectionChange={setSelectedLabs}
          getItemId={(lab) => lab.slug}
          pagination={{
            current: pagination.current,
            total: pagination.total,
            pageSize: pagination.pageSize,
            onPageChange: (page) => setPagination(prev => ({ ...prev, current: page })),
            onPageSizeChange: (size) => setPagination(prev => ({ ...prev, pageSize: size, current: 1 }))
          }}
          emptyMessage="No labs found"
        />

        {/* Create Lab Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          title="Create New Lab"
          size="lg"
        >
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Slug *
                </label>
                <Input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="e.g., intro-to-docker"
                  className="bg-gray-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title *
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Introduction to Docker"
                  className="bg-gray-800"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Summary
              </label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                placeholder="Brief description of the lab..."
                rows={3}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white resize-none"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  OS *
                </label>
                <select
                  value={formData.os}
                  onChange={(e) => setFormData(prev => ({ ...prev, os: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="ubuntu-22-04-x64">Ubuntu 22.04 x64</option>
                  <option value="ubuntu-20-04-x64">Ubuntu 20.04 x64</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Droplet Size
                </label>
                <select
                  value={formData.droplet_size}
                  onChange={(e) => setFormData(prev => ({ ...prev, droplet_size: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="s-1vcpu-1gb">1 vCPU, 1GB</option>
                  <option value="s-2vcpu-2gb">2 vCPU, 2GB</option>
                  <option value="s-2vcpu-4gb">2 vCPU, 4GB</option>
                  <option value="s-4vcpu-8gb">4 vCPU, 8GB</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Difficulty *
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Estimated Minutes *
              </label>
              <Input
                type="number"
                min={1}
                value={formData.estimated_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, estimated_minutes: parseInt(e.target.value) || 60 }))}
                className="bg-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tags
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  className="bg-gray-800 flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
                />
                <Button size="sm" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span key={tag} className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-sm flex items-center gap-1">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="text-gray-400 hover:text-white">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Prerequisites
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="text"
                  value={prerequisiteInput}
                  onChange={(e) => setPrerequisiteInput(e.target.value)}
                  placeholder="Add prerequisite..."
                  className="bg-gray-800 flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addPrerequisite()}
                />
                <Button size="sm" onClick={addPrerequisite}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.prerequisites.map(prereq => (
                  <span key={prereq} className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-sm flex items-center gap-1">
                    {prereq}
                    <button onClick={() => removePrerequisite(prereq)} className="text-gray-400 hover:text-white">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Manifest (JSON) *
              </label>
              <textarea
                value={manifestInput}
                onChange={(e) => setManifestInput(e.target.value)}
                placeholder='{"steps": [], "tasks": [], "rubric": {}}'
                rows={6}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white font-mono text-sm resize-none"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="primary"
                onClick={handleCreateLab}
                disabled={!formData.slug || !formData.title || !manifestInput.trim()}
              >
                Create Lab
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        {/* Edit Lab Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingLab(null);
            resetForm();
          }}
          title={`Edit Lab: ${editingLab?.title}`}
          size="lg"
        >
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Slug (read-only)
                </label>
                <Input
                  type="text"
                  value={formData.slug}
                  disabled
                  className="bg-gray-700 text-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-gray-800"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Summary
              </label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                placeholder="Brief description of the lab..."
                rows={3}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white resize-none"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  OS
                </label>
                <select
                  value={formData.os}
                  onChange={(e) => setFormData(prev => ({ ...prev, os: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="ubuntu-22-04-x64">Ubuntu 22.04 x64</option>
                  <option value="ubuntu-20-04-x64">Ubuntu 20.04 x64</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Droplet Size
                </label>
                <select
                  value={formData.droplet_size}
                  onChange={(e) => setFormData(prev => ({ ...prev, droplet_size: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="s-1vcpu-1gb">1 vCPU, 1GB</option>
                  <option value="s-2vcpu-2gb">2 vCPU, 2GB</option>
                  <option value="s-2vcpu-4gb">2 vCPU, 4GB</option>
                  <option value="s-4vcpu-8gb">4 vCPU, 8GB</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Difficulty
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Estimated Minutes
              </label>
              <Input
                type="number"
                min={1}
                value={formData.estimated_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, estimated_minutes: parseInt(e.target.value) || 60 }))}
                className="bg-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tags
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  className="bg-gray-800 flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
                />
                <Button size="sm" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span key={tag} className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-sm flex items-center gap-1">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="text-gray-400 hover:text-white">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Prerequisites
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="text"
                  value={prerequisiteInput}
                  onChange={(e) => setPrerequisiteInput(e.target.value)}
                  placeholder="Add prerequisite..."
                  className="bg-gray-800 flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addPrerequisite()}
                />
                <Button size="sm" onClick={addPrerequisite}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.prerequisites.map(prereq => (
                  <span key={prereq} className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-sm flex items-center gap-1">
                    {prereq}
                    <button onClick={() => removePrerequisite(prereq)} className="text-gray-400 hover:text-white">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Manifest (JSON) - Leave empty to keep current
              </label>
              <textarea
                value={manifestInput}
                onChange={(e) => setManifestInput(e.target.value)}
                placeholder='Leave empty to keep current manifest or enter new JSON'
                rows={4}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white font-mono text-sm resize-none"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="primary"
                onClick={handleEditLab}
              >
                Update Lab
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingLab(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Lab Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletingLab(null);
            setForceDelete(false);
          }}
          title="Delete Lab"
        >
          <div className="space-y-4">
            <p className="text-gray-300">
              Are you sure you want to delete lab: <strong>{deletingLab?.title}</strong>?
            </p>
            
            {deletingLab && (deletingLab.active_runs || 0) > 0 && (
              <div>
                <Alert variant="warning" title="Warning">
                  This lab has {deletingLab.active_runs || 0} active runs. Deleting will affect these instances.
                </Alert>
                <div className="mt-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={forceDelete}
                      onChange={(e) => setForceDelete(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-gray-300">Force delete (override active runs protection)</span>
                  </label>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="danger"
                onClick={handleDeleteLab}
                disabled={(deletingLab?.active_runs || 0) > 0 && !forceDelete}
              >
                Delete Lab
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingLab(null);
                  setForceDelete(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}